import React from 'react'
import { cn } from '@/utils/cn'

export interface TableColumn<T> {
  key: string
  header: React.ReactNode
  className?: string
  align?: 'left' | 'center' | 'right'
  render: (row: T, index: number) => React.ReactNode
}

export interface DataTableProps<T> {
  data: T[]
  columns: TableColumn<T>[]
  isLoading?: boolean
  emptyState?: React.ReactNode
  getRowId?: (row: T, index: number) => string
  skeletonRowCount?: number
  className?: string
}

const alignClassMap: Record<'left' | 'center' | 'right', string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
}

export function DataTable<T>({
  data,
  columns,
  isLoading,
  emptyState,
  getRowId,
  skeletonRowCount = 5,
  className,
}: DataTableProps<T>) {
  const rowsToRender = isLoading ? Array.from({ length: skeletonRowCount }) : data

  return (
    <div className={cn('w-full', className)}>
      <div className="overflow-hidden rounded-xl border border-secondary-100 bg-white shadow-sm">
        <div className="w-full overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-100">
            <thead className="bg-secondary-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={cn(
                      'px-4 py-3 text-sm font-semibold text-secondary-600',
                      alignClassMap[column.align ?? 'left'],
                      column.className,
                    )}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100 bg-white">
              {!isLoading && rowsToRender.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-10 text-center text-secondary-500">
                    {emptyState ?? <p>No records found.</p>}
                  </td>
                </tr>
              )}
              {rowsToRender.map((row, index) => {
                const key = row && getRowId ? getRowId(row as T, index) : `row-${index}`
                return (
                  <tr key={key} className={cn(isLoading && 'animate-pulse')}>
                    {columns.map((column) => (
                      <td
                        key={`${key}-${column.key}`}
                        className={cn('px-4 py-4 text-sm text-secondary-700', alignClassMap[column.align ?? 'left'], column.className)}
                      >
                        {isLoading ? <div className="h-3.5 w-3/4 rounded bg-secondary-100" /> : column.render(row as T, index)}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
